import { chain, find, get, isArray, pick, size, sortBy, toLower, toUpper, isEmpty } from "lodash"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"

export const VIEW_TYPES = {
  ABSOLUTE: "absolute",
  ACTIVE: "active",
}

export const ETF_BASE_UNSUPPORTED_MESSAGE = "ETF constituent data is not available for this model"
export const NUMBER_OF_RECENT_BASES = 10

export function computeBaseWeight(assetLongShortType) {
  return assetLongShortType === "SHORT_ONLY" ? -1 : 1
}

export const BASE_TYPES = {
  benchmark: "BENCHMARK",
  portfolio: "PORTFOLIO",
  index: "INDEX",
  researchTopic: "RESEARCH_TOPIC",
  etf: "ETF",
  basket: "SWAP",
}

export const BASKET_TYPES = ["SWAP", "BASKET"]

export const makeGetExperimentMeta = (weight) => (experiment) => ({
  id: experiment.resourceId,
  name: experiment.name,
  experimentId: experiment.id,
  type: experiment.resourceType,
  availableFrom: experiment.availableFrom,
  weight,
  ...(experiment.topic && { topic: experiment.topic }),
})

export const makeGetBenchmarkMeta = (weight) => (benchmark) => ({
  ...benchmark,
  type: BASE_TYPES.benchmark,
  weight,
})

export const makeGetIndexMeta = (weight) => (index) => {
  return {
    ...index,
    type: BASE_TYPES.index,
    weight,
  }
}

export const makeGetPortfolioMeta = (weight) => (portfolio) => ({
  ...pick(portfolio, ["id", "name", "availableFrom"]),
  type: BASE_TYPES.portfolio,
  weight,
})

export const makeGetETFMeta = (weight) => (etf) => ({
  ...pick(etf, ["id", "ticker", "mic", "internalId", "hasComposition"]),
  name: etf.description,
  type: BASE_TYPES.etf,
  weight,
})

export const makeGetBasketMeta = (weight) => (basket) => ({
  id: basket.ticker,
  name: basket.description,
  type: BASE_TYPES.basket,
  weight,
})

const makeFindAndGetExperiment = (weight, experiments) => (id, experimentId) => {
  const experiment = find(experiments, { resourceId: id, id: experimentId })
  if (!experiment) { return null }

  return makeGetExperimentMeta(weight)(experiment)
}

const makeFindAndGetBenchmark = (weight, benchmarks) => (id) => {
  let benchmark
  // [TODO] Should streamline the structure of the data where we're calling this to avoid this check
  if (isArray(benchmarks)) {
    benchmark = find(benchmarks, { id })
  } else {
    benchmark = benchmarks[id]
  }
  if (!benchmark) { return null }

  return makeGetBenchmarkMeta(weight)(benchmark)
}

const makeFindAndGetIndex = (weight, indices) => (id) => {
  const index = find(indices, { id })
  if (!index) { return null }

  return makeGetIndexMeta(weight)(index)
}

const makeFindAndGetPortfolio = (weight, portfolios) => (id) => {
  const portfolio = find(portfolios, { id })
  if (!portfolio) { return null }

  return makeGetPortfolioMeta(weight)(portfolio)
}

const makeFindAndGetETF = (weight, etfs) => (id) => {
  const etf = find(etfs, { id })
  if (!etf) { return null }

  return makeGetETFMeta(weight)(etf)
}

const makeFindAndGetBasket = (weight, baskets) => (ticker) => {
  const basket = find(baskets, { ticker })
  if (!basket) { return null }

  return makeGetBasketMeta(weight)(basket)
}

export const makeFindAndGetBaseMetadata = (weight, activeInstruments) => (base) => {
  const { id, type, experimentId, ticker, mic, alias } = base || {}
  const { experiments, benchmarks, indices, portfolios, etfs, baskets } = activeInstruments || {}
  const uppercaseType = toUpper(type)

  if (experimentId) {
    return makeFindAndGetExperiment(weight, experiments)(id, experimentId)
  }

  if (uppercaseType === BASE_TYPES.benchmark) {
    return makeFindAndGetBenchmark(weight, benchmarks)(id)
  }

  if (uppercaseType === BASE_TYPES.index) {
    return makeFindAndGetIndex(weight, indices)(id)
  }

  if (uppercaseType === BASE_TYPES.portfolio) {
    return makeFindAndGetPortfolio(weight, portfolios)(id)
  }

  if (uppercaseType === BASE_TYPES.etf) {
    // Need to account for recent instruments that use internalId as the id
    const etfId = ticker && mic ? `${ticker}.${mic}` : id
    return makeFindAndGetETF(weight, etfs)(etfId)
  }

  if (BASKET_TYPES.includes(uppercaseType)) {
    // Need to account for recent instruments that use resourceId as the id
    const basketTicker = alias ? toUpper(alias) : id
    return makeFindAndGetBasket(weight, baskets)(basketTicker)
  }

  return null
}

export const searchBase = (searchValue, weight, activeInstruments, modelSupportsComposites = true) => {
  if (!searchValue || !activeInstruments) return []

  const { experiments = [], benchmarks = [], indices = [], portfolios = [], etfs = [], baskets = [] } = activeInstruments

  const searchFilter = ({ id, name, ticker, description }) => chain([id, name, ticker, description])
    .compact()
    .join(" ")
    .toLower()
    .includes(toLower(searchValue))
    .value()

  const results = [
    // benchmarks
    ...chain(benchmarks)
      .filter(searchFilter)
      .map(makeGetBenchmarkMeta(weight))
      .value(),
    // indices
    ...chain(indices)
      .filter(searchFilter)
      .map(makeGetIndexMeta(weight))
      .value(),
    // portfolios
    ...chain(portfolios)
      .filter(searchFilter)
      .map(makeGetPortfolioMeta(weight))
      .value(),
    // experiments
    ...chain(experiments)
      .filter(searchFilter)
      .map(makeGetExperimentMeta(weight))
      .value(),
    // etfs - only include if model supports composites
    ...(modelSupportsComposites ? chain(etfs)
      .filter(searchFilter)
      .map(makeGetETFMeta(weight))
      .value() : []),
    // baskets
    ...chain(baskets)
      .filter(searchFilter)
      .map(makeGetBasketMeta(weight))
      .value(),
  ]

  return sortBy(results, "name", "asc")
}

export const computeBaseHistory = (baseHistory, weight, bases) => chain(baseHistory)
  .map(makeFindAndGetBaseMetadata(weight, bases))
  .compact()
  .value()

export const addBaseToHistory = (baseHistory, base) => chain(baseHistory)
  .filter(({ id }) => id !== get(base, "id"))
  .unshift(pick(base, ["id", "type", "experimentId"]))
  .take(5)
  .value()

// Selected base is one of (in order of precedence):
// 1. the currently manually set benchmark
// 2. the benchmark used prior to changing the model (if applicable and if it can be found in the current model)
// 3. the last used benchmark in history (preference)
export const computeSelectedBase = (baseHistory, rawSelectedBase, lastUsedBenchmark, previousModelBaseUsed) => {
  if (rawSelectedBase) {
    return rawSelectedBase
  }

  if (previousModelBaseUsed) {
    return previousModelBaseUsed
  }

  if (size(baseHistory) > 0) {
    return lastUsedBenchmark
  }

  return null
}

export const computeFullBenchmark = (benchmark, benchmarksData, assetType) => ({
  id: benchmark,
  name: get(benchmarksData[benchmark], "name", "Unknown"),
  availableFrom: get(benchmarksData[benchmark], "availableFrom"),
  type: "BENCHMARK",
  weight: computeBaseWeight(assetType),
})

export function isETFBaseUnsupported(selectedBase, modelSupportsComposites) {
  if (!selectedBase || modelSupportsComposites) {
    return false
  }
  const baseType = get(selectedBase, "type")
  return baseType === BASE_TYPES.etf
}

export function filterAndOrderBaseInstruments(recentInstruments, baseHistory) {
  const recentInstrumentBases = chain(recentInstruments)
    .filter(instrument => instrument.searchType === RECENT_INSTRUMENT_SEARCH_TYPES.BASE)
    .slice(0, NUMBER_OF_RECENT_BASES)
    .orderBy("order")
    .value()

  if (isEmpty(recentInstrumentBases)) {
    return baseHistory
  }

  return recentInstrumentBases
}
